import { FmBanner } from './FmBanner';
import { FmText, FmStack } from './FmComposition';
import { FmDataValue } from './FmDataValue';
import { FmKeyFigures } from './FmKeyFigures';
import { FmHeading } from './FmComposition';
import { FmChartPlot } from './charts/FmChart';
import type { FmBarSeries } from './charts/chartTypes';
import { FmTable } from './FmTable';
import { FmValueList } from './FmValueList';
import type {
  FmAnalysisDistributionSeries,
  FmAnalysisMetric,
  FmAnalysisSummaryViewDescriptor,
  FmAnalysisViewDescriptor,
  FmAnalysisWorkspaceDescriptor,
} from './FmAnalysisWorkspaceRoot.types';
import { useLanguage } from '@/i18n';

type WorkspaceCopy = FmAnalysisWorkspaceDescriptor['copy'];

function MetricRail({
  metrics,
  unavailable,
}: {
  metrics: readonly FmAnalysisMetric[];
  unavailable: string;
}) {
  const { language } = useLanguage();
  if (metrics.length === 0) return null;
  return (
    <FmKeyFigures
      presentation="rail"
      layout="responsive"
      items={metrics.map((metric) =>
        metric.value === null
          ? {
              id: metric.id,
              label: metric.label,
              value: null,
              unavailableReason: unavailable,
              footnote: metric.detail,
            }
          : {
              id: metric.id,
              label: metric.label,
              value: (
                <FmDataValue
                  locale={language}
                  rule={{
                    kind: 'number',
                    value: metric.value,
                    unit: metric.unit,
                    precision: metric.precision ?? 1,
                  }}
                />
              ),
              footnote: metric.detail,
            },
      )}
    />
  );
}

function distributionRows(
  reference: readonly FmAnalysisDistributionSeries[],
  comparison: readonly FmAnalysisDistributionSeries[],
): Array<Record<string, string | number>> {
  const diameters = new Set<number>();
  [...reference, ...comparison].forEach((series) => {
    series.bins.forEach(({ diameterCm }) => diameters.add(diameterCm));
  });
  return [...diameters]
    .sort((a, b) => a - b)
    .map((diameterCm) => {
      const row: Record<string, string | number> = {
        diameter: String(diameterCm),
      };
      reference.forEach((series) => {
        const bin = series.bins.find((item) => item.diameterCm === diameterCm);
        if (bin) row[`reference-${series.speciesCode}`] = bin.stemsPerHectare;
      });
      comparison.forEach((series) => {
        const bin = series.bins.find((item) => item.diameterCm === diameterCm);
        if (bin) row[`comparison-${series.speciesCode}`] = bin.stemsPerHectare;
      });
      return row;
    });
}

function SummaryView({
  view,
  copy,
}: {
  view: FmAnalysisSummaryViewDescriptor;
  copy: WorkspaceCopy;
}) {
  return (
    <FmStack gap="md">
      <FmHeading level={2} visualLevel={3}>
        {view.title}
      </FmHeading>
      <FmText tone="muted">{view.explanation}</FmText>
      <MetricRail metrics={view.metrics} unavailable={copy.unavailable} />
      <FmValueList
        density="compact"
        items={view.facts.map((fact) => ({
          id: fact.id,
          label: fact.label,
          value: fact.unavailable ? copy.unavailable : fact.value,
          isUnavailable: fact.unavailable,
        }))}
      />
    </FmStack>
  );
}

type DiameterView = Extract<FmAnalysisViewDescriptor, { kind: 'diameter' }>;

function DiameterView({ view, copy }: { view: DiameterView; copy: WorkspaceCopy }) {
  const { language } = useLanguage();
  if (!view.reference || view.state === 'unavailable') {
    return (
      <FmStack gap="md">
        <FmHeading level={2} visualLevel={3}>
          {view.title}
        </FmHeading>
        <FmText tone="muted">{view.explanation}</FmText>
        <FmBanner
          tone="info"
          title={copy.unavailable}
          description={view.reason ?? copy.unavailable}
        />
      </FmStack>
    );
  }
  const referenceSpecies: FmBarSeries[] = view.reference.map((series) => ({
    color: { by: 'species', code: series.speciesCode },
    label: `${view.referenceLabel} · ${series.speciesLabel}`,
    dataKey: `reference-${series.speciesCode}`,
    stackKey: 'reference',
    role: 'reference' as const,
  }));
  const comparisonSpecies: FmBarSeries[] = (view.comparison ?? []).map((series) => ({
    color: { by: 'species', code: series.speciesCode },
    label: `${view.comparisonLabel} · ${series.speciesLabel}`,
    dataKey: `comparison-${series.speciesCode}`,
    stackKey: 'comparison',
    role: 'result' as const,
  }));
  const series = [...referenceSpecies, ...comparisonSpecies];
  return (
    <FmStack gap="md">
      <FmHeading level={2} visualLevel={3}>
        {view.title}
      </FmHeading>
      <FmText tone="muted">{view.explanation}</FmText>
      {view.state === 'modelled_only' && view.reason ? (
        <FmBanner tone="info" title={view.reason} />
      ) : null}
      <FmChartPlot
        chart={{
          kind: 'bar',
          data: distributionRows(view.reference, view.comparison ?? []),
          xKey: 'diameter',
          xLabel: view.xLabel,
          yLabel: view.yLabel,
          series,
          formatValue: (value) =>
            new Intl.NumberFormat(language, { maximumFractionDigits: 0 }).format(value),
        }}
        ariaLabel={view.title}
        data-testid="analysis-diameter-chart"
      />
    </FmStack>
  );
}

type StructureView = Extract<FmAnalysisViewDescriptor, { kind: 'structure' }>;

function StructureView({ view, copy }: { view: StructureView; copy: WorkspaceCopy }) {
  return (
    <FmStack gap="md">
      <FmHeading level={2} visualLevel={3}>
        {view.title}
      </FmHeading>
      <FmText tone="muted">{view.explanation}</FmText>
      <MetricRail metrics={view.metrics} unavailable={copy.unavailable} />
      {view.species.length ? (
        <FmChartPlot
          chart={{
            kind: 'donut',
            slices: view.species.map((item) => ({
              id: String(item.code),
              label: item.label,
              value: item.stemsPerHectare,
              color: { by: 'species', code: item.code },
            })),
          }}
          ariaLabel={view.title}
          data-testid="analysis-structure-chart"
        />
      ) : (
        <FmBanner
          tone="info"
          title={copy.unavailable}
          description={view.unavailableReason ?? copy.unavailable}
        />
      )}
    </FmStack>
  );
}

type RelationshipView = Extract<FmAnalysisViewDescriptor, { kind: 'relationship' }>;

function RelationshipView({ view, copy }: { view: RelationshipView; copy: WorkspaceCopy }) {
  return (
    <FmStack gap="md">
      <FmHeading level={2} visualLevel={3}>
        {view.title}
      </FmHeading>
      <FmText tone="muted">{view.explanation}</FmText>
      {view.groups.length ? (
        <FmChartPlot
          chart={{
            kind: 'scatter',
            groups: view.groups.map((group) => ({
              id: String(group.code),
              label: group.label,
              color: { by: 'species', code: group.code },
              points: group.points,
            })),
            xLabel: view.xLabel,
            yLabel: view.yLabel,
          }}
          ariaLabel={view.title}
          data-testid="analysis-relationship-chart"
        />
      ) : (
        <FmBanner
          tone="info"
          title={copy.unavailable}
          description={view.unavailableReason ?? copy.unavailable}
        />
      )}
    </FmStack>
  );
}

type DataView = Extract<FmAnalysisViewDescriptor, { kind: 'data' }>;

function DataView({ view, copy }: { view: DataView; copy: WorkspaceCopy }) {
  return (
    <FmStack gap="md">
      <FmHeading level={2} visualLevel={3}>
        {view.title}
      </FmHeading>
      <FmText tone="muted">{view.explanation}</FmText>
      <FmTable
        rows={[...view.rows]}
        rowKey={(row) => row.id}
        columns={[
          { key: 'label', header: copy.tableTarget, role: 'label' },
          { key: 'source', header: copy.tableSource, role: 'source' },
          { key: 'measuredAt', header: copy.tableDate, role: 'metadata' },
          { key: 'completeness', header: copy.tableCoverage, role: 'status' },
        ]}
        density="compact"
        mobileLayout="labelled-cells"
        emptyText={view.emptyLabel}
      />
    </FmStack>
  );
}

type SatelliteIndexView = Extract<FmAnalysisViewDescriptor, { kind: 'satellite-index' }>;

function SatelliteIndexView({ view, copy }: { view: SatelliteIndexView; copy: WorkspaceCopy }) {
  return (
    <FmStack gap="md">
      <FmHeading level={2} visualLevel={3}>
        {view.label}
      </FmHeading>
      <FmText tone="muted">{view.explanation}</FmText>
      {view.coverage !== 'ready' && view.coverageReason ? (
        <FmBanner tone="info" description={view.coverageReason} />
      ) : null}
      <MetricRail metrics={view.metrics} unavailable={copy.unavailable} />
      <FmValueList
        density="compact"
        items={view.facts.map((fact) => ({
          id: fact.id,
          label: fact.label,
          value: fact.unavailable ? copy.unavailable : fact.value,
          isUnavailable: fact.unavailable,
        }))}
      />
    </FmStack>
  );
}

type ReferenceDeviationView = Extract<FmAnalysisViewDescriptor, { kind: 'reference-deviation' }>;

function ReferenceDeviationView({
  view,
  copy,
}: {
  view: ReferenceDeviationView;
  copy: WorkspaceCopy;
}) {
  return (
    <FmStack gap="md">
      <FmHeading level={2} visualLevel={3}>
        {view.title}
      </FmHeading>
      <FmText tone="muted">{view.explanation}</FmText>
      {view.coverage !== 'ready' ? (
        <FmBanner
          tone="info"
          title={copy.unavailable}
          description={view.coverageReason ?? copy.unavailable}
        />
      ) : null}
      <MetricRail metrics={view.metrics} unavailable={copy.unavailable} />
      {view.points.length ? (
        <FmChartPlot
          chart={{
            kind: 'reference-deviation',
            points: view.points,
            xLabel: view.xLabel,
            yLabel: view.yLabel,
            observedLabel: view.observedLabel,
            referenceLabel: view.referenceLabel,
            surplusLabel: view.surplusLabel,
            deficitLabel: view.deficitLabel,
          }}
          ariaLabel={view.title}
          legendTestId="analysis-reference-deviation-legend"
          data-testid="analysis-reference-deviation-chart"
        />
      ) : null}
      <FmValueList
        density="compact"
        items={view.facts.map((fact) => ({
          id: fact.id,
          label: fact.label,
          value: fact.unavailable ? copy.unavailable : fact.value,
          isUnavailable: fact.unavailable,
        }))}
      />
    </FmStack>
  );
}

export function AnalysisWorkspaceView({
  view,
  copy,
}: {
  view: FmAnalysisViewDescriptor;
  copy: WorkspaceCopy;
}) {
  switch (view.kind) {
    case 'summary':
    case 'volume':
      return <SummaryView view={view} copy={copy} />;
    case 'diameter':
      return <DiameterView view={view} copy={copy} />;
    case 'structure':
      return <StructureView view={view} copy={copy} />;
    case 'relationship':
      return <RelationshipView view={view} copy={copy} />;
    case 'data':
      return <DataView view={view} copy={copy} />;
    case 'satellite-index':
      return <SatelliteIndexView view={view} copy={copy} />;
    case 'reference-deviation':
      return <ReferenceDeviationView view={view} copy={copy} />;
  }
}
